import React, {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Link,
    useHistory
} from "react-router-dom";

import axios from "axios";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const MAX_SUBJECT_LENGTH = 150;
const MAX_MESSAGE_LENGTH = 2000;


/* =========================================================
   COMPONENT
========================================================= */

const Checkout = () => {

    const history = useHistory();
    const { t } = useTranslation();


    /* =====================================================
       STATE
    ===================================================== */

    const [options, setOptions] =
        useState([]);

    const [loadingTypes, setLoadingTypes] =
        useState(true);

    const [typeError, setTypeError] =
        useState("");

    const [loading, setLoading] =
        useState(false);


    const [depositInput, setDepositInput] =
        useState({

            subject: "",

            message: "",

            w_select: "",

            error_list: {}

        });


    /* =====================================================
       LOAD TICKET TYPES
    ===================================================== */

    const loadTicketTypes =
        async () => {

            const data = {

                key_s:
                    API_KEY

            };


            try {

                setLoadingTypes(true);

                setTypeError("");


                const response =
                    await axios.post(
                        "/new-ticket",
                        data
                    );


                const rows =
                    response?.data?.data;


                setOptions(
                    Array.isArray(rows)
                        ? rows
                        : []
                );


            } catch (error) {

                console.error(
                    "Ticket Type Error:",
                    error
                );


                setOptions([]);


                setTypeError(
                    error?.response?.data?.message ||
                    "Unable to load ticket types."
                );


            } finally {

                setLoadingTypes(false);
            }
        };


    useEffect(() => {

        loadTicketTypes();

    }, []);


    /* =====================================================
       HANDLE INPUT
    ===================================================== */

    const handleInput =
        (e) => {

            const {
                name,
                value
            } = e.target;


            /* SUBJECT LIMIT */

            if (
                name === "subject" &&
                value.length >
                MAX_SUBJECT_LENGTH
            ) {
                return;
            }


            /* MESSAGE LIMIT */

            if (
                name === "message" &&
                value.length >
                MAX_MESSAGE_LENGTH
            ) {
                return;
            }


            setDepositInput(
                previous => ({

                    ...previous,

                    [name]:
                        value,

                    error_list: {

                        ...previous.error_list,

                        [name]:
                            ""

                    }

                })
            );
        };


    /* =====================================================
       SELECTED TYPE
    ===================================================== */

    const selectedTicketType =
        useMemo(() => {

            return options.find(
                item =>
                    String(item.value) ===
                    String(
                        depositInput.w_select
                    )
            );

        }, [
            options,
            depositInput.w_select
        ]);


    /* =====================================================
       ERROR MESSAGE HELPER
    ===================================================== */

    const getError =
        field => {

            const error =
                depositInput
                    ?.error_list
                    ?.[field];


            if (
                Array.isArray(error)
            ) {

                return error[0] || "";
            }


            return error || "";
        };


    /* =====================================================
       FRONTEND VALIDATION
    ===================================================== */

    const validateForm =
        () => {

            const errors = {};


            if (
                !depositInput.w_select
            ) {

                errors.w_select =
                    "Please select a ticket type.";
            }


            if (
                !depositInput.subject.trim()
            ) {

                errors.subject =
                    "Please enter a subject.";
            }


            if (
                !depositInput.message.trim()
            ) {

                errors.message =
                    "Please enter your message.";
            }


            setDepositInput(
                previous => ({

                    ...previous,

                    error_list:
                        errors

                })
            );


            return (
                Object.keys(errors)
                    .length === 0
            );
        };


    /* =====================================================
       SUBMIT
    ===================================================== */

    const depositSubmit =
        async (e) => {

            e.preventDefault();


            if (loading) {
                return;
            }


            if (!validateForm()) {
                return;
            }


            const authToken =
                localStorage.getItem(
                    "auth_token"
                );


            if (!authToken) {

                swal(
                    "Warning",
                    "Please login again.",
                    "warning"
                );

                return;
            }


            const data = {

                subject:
                    depositInput
                        .subject
                        .trim(),

                message:
                    depositInput
                        .message
                        .trim(),

                w_select:
                    depositInput
                        .w_select,

                key_s:
                    API_KEY

            };


            try {

                setLoading(true);


                const res =
                    await axios.post(
                        "/new-ticket-post",
                        data
                    );


                /* =========================================
                   SUCCESS
                ========================================= */

                if (
                    res?.data?.status ===
                    "success"
                ) {

                    setDepositInput({

                        subject: "",

                        message: "",

                        w_select: "",

                        error_list: {}

                    });


                    await swal(
                        "Ticket Created",
                        res.data.message ||
                        "Your support ticket has been created successfully.",
                        "success"
                    );


                    history.push(
                        "/support-ticket"
                    );

                }


                /* =========================================
                   FAIL
                ========================================= */

                else if (
                    res?.data?.status ===
                    "fail"
                ) {

                    swal(
                        "Warning",
                        res.data.message ||
                        "Unable to create ticket.",
                        "warning"
                    );

                }


                /* =========================================
                   VALIDATION
                ========================================= */

                else {

                    setDepositInput(
                        previous => ({

                            ...previous,

                            error_list:
                                res?.data
                                    ?.validation_error ||
                                {}

                        })
                    );
                }


            } catch (error) {

                console.error(
                    "Create Ticket Error:",
                    error
                );


                if (
                    error?.response?.status ===
                    422
                ) {

                    setDepositInput(
                        previous => ({

                            ...previous,

                            error_list:
                                error?.response
                                    ?.data
                                    ?.errors ||
                                error?.response
                                    ?.data
                                    ?.validation_error ||
                                {}

                        })
                    );

                    return;
                }


                swal(
                    "Error",
                    error?.response?.data
                        ?.message ||
                    "Something went wrong. Please try again.",
                    "error"
                );


            } finally {

                setLoading(false);
            }
        };


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                CSS
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .create-ticket-page {

                    --primary: #5b4df7;
                    --primary-dark: #4939e8;
                    --primary-soft: #f2f0ff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --blue: #1570ef;
                    --blue-soft: #eff8ff;

                    --orange: #f79009;
                    --orange-soft: #fff7ed;

                    --red: #f04438;
                    --red-dark: #b42318;
                    --red-soft: #fef3f2;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --light: #f9fafb;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .create-ticket-container {

                    width: 100%;

                    max-width: 1000px;

                    margin: 0 auto;
                }


                /* =============================================
                   HEADER
                ============================================= */

                .create-ticket-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 22px;
                }


                .create-ticket-title {

                    display: flex;

                    align-items: center;

                    gap: 14px;
                }


                .create-ticket-title-icon {

                    width: 52px;
                    height: 52px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 15px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .create-ticket-title h2 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 26px;

                    line-height: 1.25;

                    font-weight: 800;
                }


                .create-ticket-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 13px;

                    line-height: 1.5;
                }


                .create-ticket-back {

                    min-height: 43px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 8px;

                    padding:
                        0 16px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 10px;

                    background:
                        #ffffff;

                    color:
                        var(--text2) !important;

                    font-size: 12px;

                    font-weight: 700;

                    text-decoration:
                        none !important;

                    transition:
                        .15s ease;
                }


                .create-ticket-back:hover {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary) !important;
                }


                /* =============================================
                   LAYOUT
                ============================================= */

                .create-ticket-layout {

                    display: grid;

                    grid-template-columns:
                        minmax(0, 1fr)
                        270px;

                    gap: 17px;

                    align-items: start;
                }


                /* =============================================
                   CARD
                ============================================= */

                .create-ticket-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 18px;

                    background: #ffffff;

                    box-shadow:
                        0 8px 28px
                        rgba(
                            16,
                            24,
                            40,
                            .055
                        );
                }


                .create-ticket-card-header {

                    padding:
                        19px 21px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .create-ticket-card-header h3 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .create-ticket-card-header p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 11px;

                    line-height: 1.5;
                }


                .create-ticket-card-body {

                    padding: 22px;
                }


                /* =============================================
                   INFO NOTICE
                ============================================= */

                .create-ticket-notice {

                    display: flex;

                    align-items:
                        flex-start;

                    gap: 11px;

                    padding: 13px 14px;

                    margin-bottom: 22px;

                    border:
                        1px solid
                        #d9d6fe;

                    border-radius: 11px;

                    background:
                        #faf9ff;
                }


                .create-ticket-notice-icon {

                    width: 34px;
                    height: 34px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 9px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 13px;
                }


                .create-ticket-notice strong {

                    display: block;

                    margin-bottom: 2px;

                    color:
                        var(--text2);

                    font-size: 12px;

                    font-weight: 800;
                }


                .create-ticket-notice p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 10px;

                    line-height: 1.5;
                }


                /* =============================================
                   FORM
                ============================================= */

                .create-ticket-group {

                    margin-bottom: 20px;
                }


                .create-ticket-label-row {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-bottom: 7px;
                }


                .create-ticket-label {

                    margin: 0;

                    color:
                        var(--text2);

                    font-size: 12px;

                    font-weight: 800;
                }


                .create-ticket-required {

                    color:
                        var(--red);
                }


                .create-ticket-counter {

                    color:
                        #98a2b3;

                    font-size: 9px;

                    font-weight: 600;
                }


                .create-ticket-counter.limit {

                    color:
                        var(--red);
                }


                .create-ticket-field {

                    position: relative;

                    width: 100%;
                }


                .create-ticket-field-icon {

                    position: absolute;

                    top: 50%;
                    left: 14px;

                    z-index: 2;

                    transform:
                        translateY(-50%);

                    color:
                        #98a2b3;

                    font-size: 13px;

                    pointer-events: none;
                }


                .create-ticket-input,
                .create-ticket-select {

                    width: 100%;
                    height: 49px;

                    padding:
                        0 42px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text);

                    font-family:
                        inherit;

                    font-size: 13px;

                    transition:
                        border-color .18s ease,
                        box-shadow .18s ease;
                }


                .create-ticket-select {

                    padding-right: 38px;

                    appearance: none;

                    cursor: pointer;
                }


                .create-ticket-select-arrow {

                    position: absolute;

                    top: 50%;
                    right: 15px;

                    transform:
                        translateY(-50%);

                    color:
                        var(--muted);

                    font-size: 11px;

                    pointer-events: none;
                }


                .create-ticket-input::placeholder {

                    color:
                        #98a2b3;
                }


                .create-ticket-input:focus,
                .create-ticket-select:focus,
                .create-ticket-textarea:focus {

                    border-color:
                        var(--primary);

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            91,
                            77,
                            247,
                            .08
                        );
                }


                .create-ticket-input.error,
                .create-ticket-select.error,
                .create-ticket-textarea.error {

                    border-color:
                        var(--red);

                    background:
                        #fffafa;
                }


                /* =============================================
                   SELECTED TYPE
                ============================================= */

                .create-ticket-selected-type {

                    display: flex;

                    align-items: center;

                    gap: 6px;

                    margin-top: 7px;

                    color:
                        var(--green-dark);

                    font-size: 10px;

                    font-weight: 700;
                }


                .create-ticket-selected-type i {

                    color:
                        var(--green);
                }


                /* =============================================
                   TEXTAREA
                ============================================= */

                .create-ticket-textarea {

                    display: block;

                    width: 100%;

                    min-height: 180px;

                    padding: 13px 14px;

                    resize: vertical;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text);

                    font-family:
                        inherit;

                    font-size: 13px;

                    line-height: 1.7;

                    transition:
                        border-color .18s ease,
                        box-shadow .18s ease;
                }


                .create-ticket-textarea::placeholder {

                    color:
                        #98a2b3;
                }


                /* =============================================
                   ERROR
                ============================================= */

                .create-ticket-error {

                    display: flex;

                    align-items: center;

                    gap: 5px;

                    margin-top: 6px;

                    color:
                        var(--red-dark);

                    font-size: 10px;

                    font-weight: 600;
                }


                /* =============================================
                   ACTIONS
                ============================================= */

                .create-ticket-actions {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding-top: 19px;

                    margin-top: 4px;

                    border-top:
                        1px solid
                        #eef0f4;
                }


                .create-ticket-security {

                    display: flex;

                    align-items: center;

                    gap: 6px;

                    color:
                        var(--muted);

                    font-size: 10px;
                }


                .create-ticket-submit {

                    min-width: 160px;
                    height: 46px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 8px;

                    padding:
                        0 18px;

                    border: 0;

                    border-radius: 10px;

                    outline: none;

                    background:
                        var(--primary);

                    color:
                        #ffffff;

                    font-size: 12px;

                    font-weight: 800;

                    cursor: pointer;

                    box-shadow:
                        0 6px 16px
                        rgba(
                            91,
                            77,
                            247,
                            .18
                        );

                    transition:
                        .18s ease;
                }


                .create-ticket-submit:hover:not(:disabled) {

                    background:
                        var(--primary-dark);

                    transform:
                        translateY(-1px);
                }


                .create-ticket-submit:disabled {

                    opacity: .6;

                    cursor: not-allowed;

                    transform: none;
                }


                .create-ticket-spinner {

                    width: 15px;
                    height: 15px;

                    border:
                        2px solid
                        rgba(
                            255,
                            255,
                            255,
                            .4
                        );

                    border-top-color:
                        #ffffff;

                    border-radius: 50%;

                    animation:
                        ticketCreateSpin
                        .7s linear
                        infinite;
                }


                @keyframes ticketCreateSpin {

                    to {

                        transform:
                            rotate(360deg);
                    }
                }


                /* =============================================
                   SIDE PANEL
                ============================================= */

                .create-ticket-side {

                    display: flex;

                    flex-direction: column;

                    gap: 13px;
                }


                .ticket-help-card {

                    padding: 18px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 15px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 5px 20px
                        rgba(
                            16,
                            24,
                            40,
                            .04
                        );
                }


                .ticket-help-icon {

                    width: 40px;
                    height: 40px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    margin-bottom: 13px;

                    border-radius: 11px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 15px;
                }


                .ticket-help-card.green
                .ticket-help-icon {

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);
                }


                .ticket-help-card.blue
                .ticket-help-icon {

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue);
                }


                .ticket-help-card h4 {

                    margin:
                        0 0 5px;

                    color:
                        var(--text);

                    font-size: 13px;

                    font-weight: 800;
                }


                .ticket-help-card p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 10px;

                    line-height: 1.65;
                }


                .ticket-help-list {

                    padding: 0;

                    margin:
                        11px 0 0;

                    list-style: none;
                }


                .ticket-help-list li {

                    position: relative;

                    padding-left: 16px;

                    margin-bottom: 7px;

                    color:
                        var(--muted);

                    font-size: 10px;

                    line-height: 1.5;
                }


                .ticket-help-list li:last-child {

                    margin-bottom: 0;
                }


                .ticket-help-list li::before {

                    content: "✓";

                    position: absolute;

                    top: 0;
                    left: 0;

                    color:
                        var(--green);

                    font-weight: 800;
                }


                /* =============================================
                   TYPE ERROR
                ============================================= */

                .ticket-type-error {

                    display: flex;

                    align-items:
                        flex-start;

                    gap: 8px;

                    padding: 10px;

                    margin-top: 8px;

                    border-radius: 9px;

                    background:
                        var(--red-soft);

                    color:
                        var(--red-dark);

                    font-size: 10px;

                    line-height: 1.5;
                }


                .ticket-type-retry {

                    padding: 0;

                    border: 0;

                    background:
                        transparent;

                    color:
                        var(--primary);

                    font-size: 10px;

                    font-weight: 800;

                    cursor: pointer;
                }


                /* =============================================
                   RESPONSIVE
                ============================================= */

                @media (
                    max-width: 900px
                ) {

                    .create-ticket-layout {

                        grid-template-columns:
                            1fr;
                    }


                    .create-ticket-side {

                        display: grid;

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );
                    }

                }


                @media (
                    max-width: 767px
                ) {

                    .create-ticket-page {

                        padding: 14px;

                        border-radius: 14px;
                    }


                    .create-ticket-header {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .create-ticket-title {

                        align-items:
                            flex-start;
                    }


                    .create-ticket-title-icon {

                        width: 45px;
                        height: 45px;

                        border-radius: 12px;
                    }


                    .create-ticket-title h2 {

                        font-size: 22px;
                    }


                    .create-ticket-back {

                        width: 100%;
                    }


                    .create-ticket-card-body {

                        padding: 17px;
                    }


                    .create-ticket-card-header {

                        padding:
                            17px;
                    }


                    .create-ticket-actions {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .create-ticket-submit {

                        width: 100%;
                    }


                    .create-ticket-side {

                        grid-template-columns:
                            1fr;
                    }

                }


                @media (
                    max-width: 420px
                ) {

                    .create-ticket-page {

                        padding: 9px;
                    }


                    .create-ticket-notice {

                        padding: 11px;
                    }


                    .create-ticket-textarea {

                        min-height: 160px;
                    }

                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main
                className="create-ticket-page"
            >

                <div
                    className="create-ticket-container"
                >


                    {/* =========================================
                        HEADER
                    ========================================= */}

                    <div
                        className="create-ticket-header"
                    >

                        <div
                            className="create-ticket-title"
                        >

                            <div
                                className="create-ticket-title-icon"
                            >

                                <i
                                    className="fa fa-headphones"
                                    aria-hidden="true"
                                ></i>

                            </div>


                            <div>

                                <h2>
                                    Create Support Ticket
                                </h2>

                                <p>
                                    Tell us how we can help.
                                    Our support team will review
                                    your request.
                                </p>

                            </div>

                        </div>


                        <Link
                            to="/support-ticket"
                            className="create-ticket-back"
                        >

                            <i
                                className="fa fa-arrow-left"
                                aria-hidden="true"
                            ></i>

                            All Tickets

                        </Link>

                    </div>


                    {/* =========================================
                        LAYOUT
                    ========================================= */}

                    <div
                        className="create-ticket-layout"
                    >


                        {/* =====================================
                            MAIN FORM
                        ===================================== */}

                        <div
                            className="create-ticket-card"
                        >


                            {/* HEADER */}

                            <div
                                className="create-ticket-card-header"
                            >

                                <h3>
                                    Submit a New Request
                                </h3>

                                <p>
                                    Complete the form below with
                                    the details of your issue.
                                </p>

                            </div>


                            {/* BODY */}

                            <div
                                className="create-ticket-card-body"
                            >


                                {/* INFO */}

                                <div
                                    className="create-ticket-notice"
                                >

                                    <div
                                        className="create-ticket-notice-icon"
                                    >

                                        <i
                                            className="fa fa-info"
                                            aria-hidden="true"
                                        ></i>

                                    </div>


                                    <div>

                                        <strong>
                                            Provide clear details
                                        </strong>

                                        <p>
                                            Select the correct ticket
                                            type and describe your issue
                                            clearly so our support team
                                            can assist you faster.
                                        </p>

                                    </div>

                                </div>


                                {/* =============================
                                    FORM
                                ============================= */}

                                <form
                                    onSubmit={
                                        depositSubmit
                                    }
                                    noValidate
                                >


                                    {/* =========================
                                        TICKET TYPE
                                    ========================= */}

                                    <div
                                        className="create-ticket-group"
                                    >

                                        <div
                                            className="create-ticket-label-row"
                                        >

                                            <label
                                                className="create-ticket-label"
                                                htmlFor="w_select"
                                            >
                                                Ticket Type{" "}

                                                <span
                                                    className="create-ticket-required"
                                                >
                                                    *
                                                </span>
                                            </label>

                                        </div>


                                        <div
                                            className="create-ticket-field"
                                        >

                                            <span
                                                className="create-ticket-field-icon"
                                            >
                                                <i
                                                    className="fa fa-list-alt"
                                                    aria-hidden="true"
                                                ></i>
                                            </span>


                                            <select
                                                id="w_select"
                                                name="w_select"
                                                value={
                                                    depositInput.w_select
                                                }
                                                onChange={
                                                    handleInput
                                                }
                                                disabled={
                                                    loadingTypes
                                                }
                                                className={
                                                    `create-ticket-select ${
                                                        getError(
                                                            "w_select"
                                                        )
                                                            ? "error"
                                                            : ""
                                                    }`
                                                }
                                            >

                                                <option value="">

                                                    {loadingTypes
                                                        ? "Loading ticket types..."
                                                        : (
                                                            t(
                                                                "pro_with_sel_get"
                                                            ) ||
                                                            "Select Ticket Type"
                                                        )
                                                    }

                                                </option>


                                                {options.map(
                                                    (
                                                        item,
                                                        index
                                                    ) => (

                                                        <option
                                                            key={
                                                                item.value ||
                                                                index
                                                            }
                                                            value={
                                                                item.value
                                                            }
                                                        >
                                                            {
                                                                item.label
                                                            }
                                                        </option>

                                                    )
                                                )}

                                            </select>


                                            <span
                                                className="create-ticket-select-arrow"
                                            >
                                                <i
                                                    className="fa fa-angle-down"
                                                    aria-hidden="true"
                                                ></i>
                                            </span>

                                        </div>


                                        {selectedTicketType && (

                                            <div
                                                className="create-ticket-selected-type"
                                            >

                                                <i
                                                    className="fa fa-check-circle"
                                                    aria-hidden="true"
                                                ></i>

                                                Selected:{" "}

                                                {
                                                    selectedTicketType.label
                                                }

                                            </div>

                                        )}


                                        {typeError && (

                                            <div
                                                className="ticket-type-error"
                                            >

                                                <i
                                                    className="fa fa-exclamation-circle"
                                                    aria-hidden="true"
                                                ></i>


                                                <div>

                                                    {typeError}

                                                    {" "}

                                                    <button
                                                        type="button"
                                                        className="ticket-type-retry"
                                                        onClick={
                                                            loadTicketTypes
                                                        }
                                                    >
                                                        Try again
                                                    </button>

                                                </div>

                                            </div>

                                        )}


                                        {getError(
                                            "w_select"
                                        ) && (

                                            <div
                                                className="create-ticket-error"
                                            >

                                                <i
                                                    className="fa fa-exclamation-circle"
                                                    aria-hidden="true"
                                                ></i>

                                                {
                                                    getError(
                                                        "w_select"
                                                    )
                                                }

                                            </div>

                                        )}

                                    </div>


                                    {/* =========================
                                        SUBJECT
                                    ========================= */}

                                    <div
                                        className="create-ticket-group"
                                    >

                                        <div
                                            className="create-ticket-label-row"
                                        >

                                            <label
                                                className="create-ticket-label"
                                                htmlFor="ticket_subject"
                                            >
                                                Subject{" "}

                                                <span
                                                    className="create-ticket-required"
                                                >
                                                    *
                                                </span>
                                            </label>


                                            <span
                                                className={
                                                    `create-ticket-counter ${
                                                        depositInput
                                                            .subject
                                                            .length >
                                                        MAX_SUBJECT_LENGTH *
                                                            0.9
                                                            ? "limit"
                                                            : ""
                                                    }`
                                                }
                                            >

                                                {
                                                    depositInput
                                                        .subject
                                                        .length
                                                }

                                                /

                                                {
                                                    MAX_SUBJECT_LENGTH
                                                }

                                            </span>

                                        </div>


                                        <div
                                            className="create-ticket-field"
                                        >

                                            <span
                                                className="create-ticket-field-icon"
                                            >
                                                <i
                                                    className="fa fa-tag"
                                                    aria-hidden="true"
                                                ></i>
                                            </span>


                                            <input
                                                type="text"
                                                id="ticket_subject"
                                                name="subject"
                                                value={
                                                    depositInput.subject
                                                }
                                                onChange={
                                                    handleInput
                                                }
                                                placeholder="Enter a short subject"
                                                autoComplete="off"
                                                className={
                                                    `create-ticket-input ${
                                                        getError(
                                                            "subject"
                                                        )
                                                            ? "error"
                                                            : ""
                                                    }`
                                                }
                                            />

                                        </div>


                                        {getError(
                                            "subject"
                                        ) && (

                                            <div
                                                className="create-ticket-error"
                                            >

                                                <i
                                                    className="fa fa-exclamation-circle"
                                                    aria-hidden="true"
                                                ></i>

                                                {
                                                    getError(
                                                        "subject"
                                                    )
                                                }

                                            </div>

                                        )}

                                    </div>


                                    {/* =========================
                                        MESSAGE
                                    ========================= */}

                                    <div
                                        className="create-ticket-group"
                                    >

                                        <div
                                            className="create-ticket-label-row"
                                        >

                                            <label
                                                className="create-ticket-label"
                                                htmlFor="ticket_message"
                                            >
                                                Message{" "}

                                                <span
                                                    className="create-ticket-required"
                                                >
                                                    *
                                                </span>
                                            </label>


                                            <span
                                                className={
                                                    `create-ticket-counter ${
                                                        depositInput
                                                            .message
                                                            .length >
                                                        MAX_MESSAGE_LENGTH *
                                                            0.9
                                                            ? "limit"
                                                            : ""
                                                    }`
                                                }
                                            >

                                                {
                                                    depositInput
                                                        .message
                                                        .length
                                                }

                                                /

                                                {
                                                    MAX_MESSAGE_LENGTH
                                                }

                                            </span>

                                        </div>


                                        <textarea
                                            id="ticket_message"
                                            name="message"
                                            value={
                                                depositInput.message
                                            }
                                            onChange={
                                                handleInput
                                            }
                                            className={
                                                `create-ticket-textarea ${
                                                    getError(
                                                        "message"
                                                    )
                                                        ? "error"
                                                        : ""
                                                }`
                                            }
                                            placeholder="Describe your issue or question in detail..."
                                            rows="8"
                                            spellCheck="true"
                                        />


                                        {getError(
                                            "message"
                                        ) && (

                                            <div
                                                className="create-ticket-error"
                                            >

                                                <i
                                                    className="fa fa-exclamation-circle"
                                                    aria-hidden="true"
                                                ></i>

                                                {
                                                    getError(
                                                        "message"
                                                    )
                                                }

                                            </div>

                                        )}

                                    </div>


                                    {/* =========================
                                        ACTION
                                    ========================= */}

                                    <div
                                        className="create-ticket-actions"
                                    >

                                        <div
                                            className="create-ticket-security"
                                        >

                                            <i
                                                className="fa fa-lock"
                                                aria-hidden="true"
                                            ></i>

                                            Your request is securely
                                            submitted to our support team.

                                        </div>


                                        <button
                                            type="submit"
                                            className="create-ticket-submit"
                                            disabled={
                                                loading
                                            }
                                        >

                                            {loading ? (

                                                <Fragment>

                                                    <span
                                                        className="create-ticket-spinner"
                                                    ></span>

                                                    Submitting...

                                                </Fragment>

                                            ) : (

                                                <Fragment>

                                                    Submit Ticket

                                                    <i
                                                        className="fa fa-paper-plane"
                                                        aria-hidden="true"
                                                    ></i>

                                                </Fragment>
                                            )}

                                        </button>

                                    </div>

                                </form>

                            </div>

                        </div>


                        {/* =====================================
                            RIGHT SIDE
                        ===================================== */}

                        <aside
                            className="create-ticket-side"
                        >


                            {/* HELP */}

                            <div
                                className="ticket-help-card"
                            >

                                <div
                                    className="ticket-help-icon"
                                >

                                    <i
                                        className="fa fa-lightbulb-o"
                                        aria-hidden="true"
                                    ></i>

                                </div>


                                <h4>
                                    Before You Submit
                                </h4>


                                <p>
                                    A clear request helps the
                                    support team understand your
                                    issue quickly.
                                </p>


                                <ul
                                    className="ticket-help-list"
                                >

                                    <li>
                                        Select the correct ticket type
                                    </li>

                                    <li>
                                        Use a clear subject
                                    </li>

                                    <li>
                                        Explain the issue in detail
                                    </li>

                                    <li>
                                        Avoid submitting duplicate tickets
                                    </li>

                                </ul>

                            </div>


                            {/* SUPPORT */}

                            <div
                                className="ticket-help-card green"
                            >

                                <div
                                    className="ticket-help-icon"
                                >

                                    <i
                                        className="fa fa-comments"
                                        aria-hidden="true"
                                    ></i>

                                </div>


                                <h4>
                                    Track Your Conversation
                                </h4>


                                <p>
                                    After submitting, open the ticket
                                    from your Support Tickets page to
                                    view replies and continue the
                                    conversation.
                                </p>

                            </div>


                            {/* SECURITY */}

                            <div
                                className="ticket-help-card blue"
                            >

                                <div
                                    className="ticket-help-icon"
                                >

                                    <i
                                        className="fa fa-shield"
                                        aria-hidden="true"
                                    ></i>

                                </div>


                                <h4>
                                    Security Reminder
                                </h4>


                                <p>
                                    Do not include your password,
                                    OTP, private key or other
                                    sensitive login credentials in
                                    your support message.
                                </p>

                            </div>

                        </aside>

                    </div>

                </div>

            </main>

        </Fragment>
    );
};


export default Checkout;